import type { DemandSegmentModel } from '@/shared/generated'

import { AudioWaveformIcon, InfoIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import {
	buildDemandSeries,
	formatHistoryDuration,
} from '@/entities/mid-term-prediction'

import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import { Skeleton } from '@/shared/components/ui/skeleton'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/shared/components/ui/tooltip'
import { cn } from '@/shared/lib/utils'
import { formatNumber, NumberFormats } from '@/shared/tools/locale'

import { patternLabel } from '../lib/labels'
import { DemandBarsChart } from './DemandBarsChart'
import { DemandQuadrant } from './DemandQuadrant'
import { InterpretationCard } from './InterpretationCard'

const PATTERN_BADGE: Record<
	DemandSegmentModel['pattern'],
	'success' | 'warning'
> = {
	smooth: 'success',
	erratic: 'success',
	intermittent: 'warning',
	lumpy: 'warning',
}

const PATTERN_REASON: Record<
	DemandSegmentModel['pattern'],
	{ key: string; defaultValue: string }
> = {
	smooth: {
		key: 'midterm_forecast.pattern_reason.smooth',
		defaultValue:
			'Low, regular day-to-day variation. Ideal for daily forecasting.',
	},
	erratic: {
		key: 'midterm_forecast.pattern_reason.erratic',
		defaultValue:
			'Regular order timing but variable quantities. Still forecastable daily.',
	},
	intermittent: {
		key: 'midterm_forecast.pattern_reason.intermittent',
		defaultValue:
			'Sales arrive in sparse, irregular spikes with many zero-sale periods.',
	},
	lumpy: {
		key: 'midterm_forecast.pattern_reason.lumpy',
		defaultValue:
			'Sparse, irregular orders with large swings — both timing and size vary.',
	},
}

const DEFAULT_PATTERN_BADGE: 'success' | 'warning' = 'warning'

const DEFAULT_PATTERN_REASON = {
	key: 'midterm_forecast.pattern_reason.unknown',
	defaultValue: 'Unable to determine why this demand pattern was assigned.',
}

const getPatternBadge = (pattern: DemandSegmentModel['pattern']) =>
	PATTERN_BADGE[pattern] ?? DEFAULT_PATTERN_BADGE

const getPatternReason = (pattern: DemandSegmentModel['pattern']) =>
	PATTERN_REASON[pattern] ?? DEFAULT_PATTERN_REASON

export const DemandPatternCard = ({
	demand,
	blocking,
	isPending,
	isError,
}: {
	demand: DemandSegmentModel | null | undefined
	blocking: boolean
	isPending: boolean
	isError: boolean
}) => {
	const { t } = useTranslation()

	if (isPending) {
		return (
			<Skeleton className='h-16 w-full' data-testid='mtf-demand-skeleton' />
		)
	}

	if (isError) {
		return (
			<p
				className='rounded-lg border p-3 text-sm text-muted-foreground'
				data-testid='mtf-demand-error'
			>
				{t('midterm_forecast.demand_error', {
					defaultValue: 'Failed to load the demand pattern.',
				})}
			</p>
		)
	}

	if (!demand) {
		return (
			<p
				className='rounded-lg border p-3 text-sm text-muted-foreground'
				data-testid='mtf-demand-empty'
			>
				{t('midterm_forecast.demand_empty', {
					defaultValue: 'No demand data for this pricing line yet.',
				})}
			</p>
		)
	}

	const series = buildDemandSeries(demand.sales_quantity_daily)
	const history = formatHistoryDuration(demand.history_days)
	const patternReason = getPatternReason(demand.pattern)
	const metrics = [
		{
			id: 'cv',
			label: t('midterm_forecast.metric_cv', { defaultValue: 'CV — quantity' }),
			value: formatNumber(demand.cv, NumberFormats.Fixed),
		},
		{
			id: 'adi',
			label: t('midterm_forecast.metric_adi', { defaultValue: 'ADI — timing' }),
			value: formatNumber(demand.adi, NumberFormats.Fixed),
		},
		{
			id: 'zero-days',
			label: t('midterm_forecast.metric_zero_days', {
				defaultValue: 'Zero-sale days',
			}),
			value: formatNumber(demand.zero_sale_rate * 100, NumberFormats.Percent),
		},
		{
			id: 'history',
			label: t('midterm_forecast.metric_history', { defaultValue: 'History' }),
			value:
				history.unit === 'months'
					? t('midterm_forecast.duration_months', {
							defaultValue: '{count} months',
							defaultValue_one: '{count} month',
							count: history.value,
						})
					: t('midterm_forecast.duration_weeks', {
							defaultValue: '{count} weeks',
							defaultValue_one: '{count} week',
							count: history.value,
						}),
		},
	]

	return (
		<InterpretationCard
			icon={
				<AudioWaveformIcon
					className={cn(
						'size-4',
						getPatternBadge(demand.pattern) === 'success'
							? 'text-success'
							: 'text-warning'
					)}
				/>
			}
			title={t('midterm_forecast.demand_pattern', {
				defaultValue: 'Demand pattern',
			})}
			badge={
				<Badge
					variant={getPatternBadge(demand.pattern)}
					data-testid='mtf-demand-pattern-badge'
				>
					{patternLabel(demand.pattern, t)}
				</Badge>
			}
			blocking={blocking}
			blockingTestId='mtf-demand-blocking'
			headerExtra={
				<Tooltip>
					<TooltipTrigger asChild>
						<Button
							variant='ghost'
							size='icon-sm'
							data-testid='mtf-demand-info'
							aria-label={t('midterm_forecast.how_computed', {
								defaultValue: 'How this is computed',
							})}
						>
							<InfoIcon className='size-3.5 text-muted-foreground' />
						</Button>
					</TooltipTrigger>
					<TooltipContent className='flex w-72 max-w-72 flex-col items-stretch p-3 text-left'>
						<p className='mb-2 text-xs font-semibold'>
							{t('midterm_forecast.how_computed', {
								defaultValue: 'How this is computed',
							})}
						</p>
						<DemandQuadrant active={demand.pattern} className='mb-2' />
						<p className='text-xs leading-relaxed text-background/70'>
							{t('midterm_forecast.quadrant_doc', {
								defaultValue:
									'Lines are classified on two axes — how regularly orders arrive (ADI) and how much the quantity varies (CV²). Smooth and erratic demand can be forecast daily; intermittent and lumpy cannot.',
							})}
						</p>
					</TooltipContent>
				</Tooltip>
			}
			mini={
				<DemandBarsChart
					points={series.points}
					windowStartIndex={series.windowStartIndex}
				/>
			}
			toggleTestId='mtf-demand-toggle'
			data-testid='mtf-demand-card'
		>
			<p className='text-xs text-muted-foreground'>
				{t(patternReason.key, { defaultValue: patternReason.defaultValue })}
			</p>
			<div>
				<p className='mb-1 text-xs font-bold tracking-wide text-muted-foreground uppercase'>
					{t('midterm_forecast.daily_units', {
						defaultValue: 'Daily units',
					})}
				</p>
				<div className='rounded-lg border p-2.5'>
					<DemandBarsChart
						points={series.points}
						windowStartIndex={series.windowStartIndex}
						height={140}
						variant='detailed'
						data-testid='mtf-demand-bars-big'
					/>
				</div>
			</div>
			<DemandQuadrant active={demand.pattern} />
			<div className='grid grid-cols-2 gap-2'>
				{metrics.map(({ id, label, value }) => (
					<div
						key={id}
						className='rounded-lg bg-secondary/50 p-2.5'
						data-testid={`mtf-demand-metric-${id}`}
					>
						<p className='text-xs text-muted-foreground'>{label}</p>
						<p className='text-sm font-bold tabular-nums'>{value}</p>
					</div>
				))}
			</div>
		</InterpretationCard>
	)
}
